import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

export function useFeedbackBadges() {
  const { user } = useAuth();

  const { data: recentFeedbackCount = 0 } = useQuery({
    queryKey: ["recent-feedback-count", user?.id],
    queryFn: async () => {
      if (!user?.id) return 0;
      
      const { data: profile } = await supabase
        .from("profiles")
        .select("last_feedback_viewed_at")
        .eq("id", user.id)
        .single();

      const { data: pages } = await supabase
        .from("feedback_pages")
        .select("id")
        .eq("user_id", user.id);

      if (!pages || pages.length === 0) return 0;

      const pageIds = pages.map(p => p.id);
      const queryBuilder = supabase
        .from("feedback_submissions")
        .select("*", { count: "exact", head: true })
        .in("page_id", pageIds);

      if (profile?.last_feedback_viewed_at) {
        queryBuilder.gt("created_at", profile.last_feedback_viewed_at);
      }

      const { count } = await queryBuilder;
      return count || 0;
    },
    enabled: !!user?.id,
    refetchInterval: 30000,
    staleTime: 10000,
  });

  const { data: pagesNeedingAttention = 0 } = useQuery({
    queryKey: ["pages-needing-attention", user?.id],
    queryFn: async () => {
      if (!user?.id) return 0;
      
      const { data: profile } = await supabase
        .from("profiles")
        .select("last_pages_viewed_at")
        .eq("id", user.id)
        .single();

      const queryBuilder = supabase
        .from("feedback_pages")
        .select("*", { count: "exact", head: true })
        .eq("user_id", user.id);

      if (profile?.last_pages_viewed_at) {
        queryBuilder.gt("created_at", profile.last_pages_viewed_at);
      }

      const { count } = await queryBuilder;
      return count || 0;
    },
    enabled: !!user?.id,
    refetchInterval: 30000,
    staleTime: 10000,
  });

  const getBadgeCount = (type: "feedback" | "pages") => {
    if (type === "feedback" && recentFeedbackCount > 0) return recentFeedbackCount;
    if (type === "pages" && pagesNeedingAttention > 0) return pagesNeedingAttention;
    return null;
  };

  return {
    recentFeedbackCount,
    pagesNeedingAttention,
    getBadgeCount,
  };
}
